import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { isNoopPayrollExecuteScript } from "@/web-archive/payrollArchiveAllowlist";

interface ArchivalModeContextType {
  isArchivalMode: boolean;
  setIsArchivalMode: (value: boolean) => void;
}

const ArchivalModeContext = createContext<ArchivalModeContextType | undefined>(undefined);

/** Read-only payroll scripts for web ZIP mode: downloads + ageing helpers (see sharePointWebShim createWebArchivePayrollBridge). */
function isPayrollScriptAllowedInWebArchive(scriptKey: string): boolean {
  if (scriptKey.startsWith("download_")) return true;
  if (scriptKey === "execute_ageing_analysis_download_csv") return true;
  if (scriptKey === "execute_ageing_analysis_list_files") return true;
  if (scriptKey === "execute_ageing_analysis_check_file_exists") return true;
  if (isNoopPayrollExecuteScript(scriptKey)) return true;
  return false;
}

export function ArchivalModeProvider({ children }: { children: ReactNode }) {
  const [isArchivalMode, setIsArchivalMode] = useState(() =>
    typeof sessionStorage !== "undefined" && sessionStorage.getItem("web_archive_mode") === "1",
  );

  // Intercept backend calls when in archival mode using try-catch for read-only properties
  useEffect(() => {
    if (isArchivalMode && typeof window !== 'undefined') {
      // Store original functions
      const originalPayrollRun = (window as any).payroll?.run;
      const originalSharePointUpload = (window as any).sharePointAPI?.uploadFile;
      const originalSharePointSave = (window as any).sharePointAPI?.saveExceptionList;
      const originalSharePointUpdate = (window as any).sharePointAPI?.updateExceptionTestJson;
      const originalSharePointUploadHeadcount = (window as any).sharePointAPI?.uploadHeadcountReconciliationUserRows;

      // Try to override window.payroll.run to prevent backend calls (allow read-only ZIP shim scripts)
      if ((window as any).payroll && originalPayrollRun) {
        try {
          (window as any).payroll.run = async (...args: any[]) => {
            const scriptKey = typeof args[0] === "string" ? args[0] : "";
            if (isPayrollScriptAllowedInWebArchive(scriptKey)) {
              return originalPayrollRun(...args);
            }
            console.warn("Archival Mode: Blocked payroll.run call");
            return { ok: false, error: "Archival Mode: Backend calls are disabled" };
          };
        } catch (error) {
          console.warn('Cannot override payroll.run (read-only), buttons should be disabled instead');
        }
      }

      // Try to override SharePoint upload/save methods
      if ((window as any).sharePointAPI) {
        if (originalSharePointUpload) {
          try {
            (window as any).sharePointAPI.uploadFile = async (...args: any[]) => {
              console.warn('Archival Mode: Blocked SharePoint uploadFile call');
              return { success: false, error: 'Archival Mode: Uploads are disabled' };
            };
          } catch (error) {
            console.warn('Cannot override sharePointAPI.uploadFile (read-only), buttons should be disabled instead');
          }
        }
        if (originalSharePointSave) {
          try {
            (window as any).sharePointAPI.saveExceptionList = async (...args: any[]) => {
              console.warn('Archival Mode: Blocked SharePoint saveExceptionList call');
              return { success: false, error: 'Archival Mode: Saves are disabled' };
            };
          } catch (error) {
            console.warn('Cannot override sharePointAPI.saveExceptionList (read-only), buttons should be disabled instead');
          }
        }
        if (originalSharePointUpdate) {
          try {
            (window as any).sharePointAPI.updateExceptionTestJson = async (...args: any[]) => {
              console.warn('Archival Mode: Blocked SharePoint updateExceptionTestJson call');
              return { success: false, error: 'Archival Mode: Updates are disabled' };
            };
          } catch (error) {
            console.warn('Cannot override sharePointAPI.updateExceptionTestJson (read-only), buttons should be disabled instead');
          }
        }
        if (originalSharePointUploadHeadcount) {
          try {
            (window as any).sharePointAPI.uploadHeadcountReconciliationUserRows = async (...args: any[]) => {
              console.warn('Archival Mode: Blocked SharePoint uploadHeadcountReconciliationUserRows call');
              return { success: false, error: 'Archival Mode: Uploads are disabled' };
            };
          } catch (error) {
            console.warn('Cannot override sharePointAPI.uploadHeadcountReconciliationUserRows (read-only), buttons should be disabled instead');
          }
        }
      }

      // Cleanup: restore original functions when archival mode is disabled
      return () => {
        if ((window as any).payroll && originalPayrollRun) {
          try {
            (window as any).payroll.run = originalPayrollRun;
          } catch (error) {
            // Property is read-only, cannot restore
          }
        }
        if ((window as any).sharePointAPI) {
          if (originalSharePointUpload) {
            try {
              (window as any).sharePointAPI.uploadFile = originalSharePointUpload;
            } catch (error) {
              // Property is read-only, cannot restore
            }
          }
          if (originalSharePointSave) {
            try {
              (window as any).sharePointAPI.saveExceptionList = originalSharePointSave;
            } catch (error) {
              // Property is read-only, cannot restore
            }
          }
          if (originalSharePointUpdate) {
            try {
              (window as any).sharePointAPI.updateExceptionTestJson = originalSharePointUpdate;
            } catch (error) {
              // Property is read-only, cannot restore
            }
          }
          if (originalSharePointUploadHeadcount) {
            try {
              (window as any).sharePointAPI.uploadHeadcountReconciliationUserRows = originalSharePointUploadHeadcount;
            } catch (error) {
              // Property is read-only, cannot restore
            }
          }
        }
      };
    }
  }, [isArchivalMode]);

  return (
    <ArchivalModeContext.Provider
      value={{
        isArchivalMode,
        setIsArchivalMode,
      }}
    >
      {children}
    </ArchivalModeContext.Provider>
  );
}

export function useArchivalMode() {
  const context = useContext(ArchivalModeContext);
  if (context === undefined) {
    throw new Error("useArchivalMode must be used within an ArchivalModeProvider");
  }
  return context;
}

